import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';

// Collection names (under users/{userId}/)
const COLLECTIONS = {
  students: 'students',
  parents: 'parents',
  faculty: 'faculty'
};

// Helper to get user-scoped collection reference
const getUserCollection = (userId, collectionName) => {
  if (!userId) throw new Error('userId is required');
  return collection(db, 'users', userId, collectionName);
};

// Helper to get user-scoped document reference
const getUserDoc = (userId, collectionName, docId) => {
  if (!userId) throw new Error('userId is required');
  return doc(db, 'users', userId, collectionName, docId);
};

// ============================================
// GET ALL FUNCTIONS
// ============================================

export async function getAllStudents(userId) {
  const snapshot = await getDocs(getUserCollection(userId, COLLECTIONS.students));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function getAllParents(userId) {
  const snapshot = await getDocs(getUserCollection(userId, COLLECTIONS.parents));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function getAllFaculty(userId) {
  const snapshot = await getDocs(getUserCollection(userId, COLLECTIONS.faculty));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// ============================================
// ADD FUNCTIONS
// ============================================

export async function addStudent(userId, data) {
  const colRef = getUserCollection(userId, COLLECTIONS.students);
  const docRef = doc(colRef);
  await setDoc(docRef, {
    name: data.name || '',
    teacher: data.teacher || '',
    grade: data.grade || '',
    barcode: data.barcode || '',
    pin: data.pin || '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  return docRef.id;
}

export async function addParent(userId, data) {
  const colRef = getUserCollection(userId, COLLECTIONS.parents);
  const docRef = doc(colRef);
  await setDoc(docRef, {
    name: data.name || '',
    children: data.children || [],
    grade: data.grade || '',
    teacher: data.teacher || '',
    barcode: data.barcode || '',
    pin: data.pin || '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  return docRef.id;
}

export async function addFaculty(userId, data) {
  const colRef = getUserCollection(userId, COLLECTIONS.faculty);
  const docRef = doc(colRef);
  await setDoc(docRef, {
    name: data.name || '',
    role: data.role || '',
    grade: data.grade || '',
    barcode: data.barcode || '',
    pin: data.pin || '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  return docRef.id;
}

// ============================================
// UPDATE FUNCTIONS
// ============================================

export async function updateStudent(userId, id, data) {
  const docRef = getUserDoc(userId, COLLECTIONS.students, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: new Date().toISOString()
  });
}

export async function updateParent(userId, id, data) {
  const docRef = getUserDoc(userId, COLLECTIONS.parents, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: new Date().toISOString()
  });
}

export async function updateFaculty(userId, id, data) {
  const docRef = getUserDoc(userId, COLLECTIONS.faculty, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: new Date().toISOString()
  });
}

// ============================================
// DELETE FUNCTIONS
// ============================================

export async function deleteStudent(userId, id) {
  await deleteDoc(getUserDoc(userId, COLLECTIONS.students, id));
}

export async function deleteParent(userId, id) {
  await deleteDoc(getUserDoc(userId, COLLECTIONS.parents, id));
}

export async function deleteFaculty(userId, id) {
  await deleteDoc(getUserDoc(userId, COLLECTIONS.faculty, id));
}

// ============================================
// BULK IMPORT FUNCTIONS
// ============================================

// Firestore batch limit is 500 operations
const BATCH_LIMIT = 500;

export async function bulkImportStudents(userId, studentsArray) {
  const results = { success: 0, failed: 0, errors: [] };

  // Process in chunks of BATCH_LIMIT
  for (let i = 0; i < studentsArray.length; i += BATCH_LIMIT) {
    const chunk = studentsArray.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(db);

    chunk.forEach(student => {
      const colRef = getUserCollection(userId, COLLECTIONS.students);
      const docRef = doc(colRef);
      batch.set(docRef, {
        name: student.name || student.student || '',
        teacher: student.teacher || '',
        grade: student.grade || '',
        barcode: student.barcode || '',
        pin: student.pin || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    });

    try {
      await batch.commit();
      results.success += chunk.length;
    } catch (error) {
      results.failed += chunk.length;
      results.errors.push({ batch: Math.floor(i / BATCH_LIMIT), error: error.message });
    }
  }

  return results;
}

export async function bulkImportParents(userId, parentsArray) {
  const results = { success: 0, failed: 0, errors: [] };

  for (let i = 0; i < parentsArray.length; i += BATCH_LIMIT) {
    const chunk = parentsArray.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(db);

    chunk.forEach(parent => {
      const colRef = getUserCollection(userId, COLLECTIONS.parents);
      const docRef = doc(colRef);
      batch.set(docRef, {
        name: parent.name || '',
        children: parent.children || [],
        grade: parent.grade || '',
        teacher: parent.teacher || '',
        barcode: parent.barcode || '',
        pin: parent.pin || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    });

    try {
      await batch.commit();
      results.success += chunk.length;
    } catch (error) {
      results.failed += chunk.length;
      results.errors.push({ batch: Math.floor(i / BATCH_LIMIT), error: error.message });
    }
  }

  return results;
}

export async function bulkImportFaculty(userId, facultyArray) {
  const results = { success: 0, failed: 0, errors: [] };

  for (let i = 0; i < facultyArray.length; i += BATCH_LIMIT) {
    const chunk = facultyArray.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(db);

    chunk.forEach(faculty => {
      const colRef = getUserCollection(userId, COLLECTIONS.faculty);
      const docRef = doc(colRef);
      batch.set(docRef, {
        name: faculty.name || '',
        role: faculty.role || '',
        grade: faculty.grade || '',
        barcode: faculty.barcode || '',
        pin: faculty.pin || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    });

    try {
      await batch.commit();
      results.success += chunk.length;
    } catch (error) {
      results.failed += chunk.length;
      results.errors.push({ batch: Math.floor(i / BATCH_LIMIT), error: error.message });
    }
  }

  return results;
}

// ============================================
// SEARCH & LOOKUP FUNCTIONS
// ============================================

export async function getPatronType(userId, name) {
  if (!name) return 'unknown';

  const searchName = name.toLowerCase().trim();

  // Check students
  const students = await getAllStudents(userId);
  const studentMatch = students.find(s =>
    s.name.toLowerCase() === searchName
  );
  if (studentMatch) return 'student';

  // Check parents
  const parents = await getAllParents(userId);
  const parentMatch = parents.find(p =>
    p.name.toLowerCase() === searchName
  );
  if (parentMatch) return 'parent';

  // Check faculty
  const faculty = await getAllFaculty(userId);
  const facultyMatch = faculty.find(f =>
    f.name.toLowerCase() === searchName
  );
  if (facultyMatch) return 'faculty';

  return 'unknown';
}

export async function searchRoster(userId, searchTerm) {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return { students: [], parents: [], faculty: [], total: 0 };
  }

  const term = searchTerm.toLowerCase().trim();

  // Fetch all collections
  const [students, parents, faculty] = await Promise.all([
    getAllStudents(userId),
    getAllParents(userId),
    getAllFaculty(userId)
  ]);

  // Filter students
  const matchedStudents = students.filter(s =>
    s.name.toLowerCase().includes(term) ||
    s.teacher.toLowerCase().includes(term) ||
    s.grade.toLowerCase().includes(term) ||
    (s.barcode && s.barcode.includes(term))
  );

  // Filter parents
  const matchedParents = parents.filter(p =>
    p.name.toLowerCase().includes(term) ||
    (p.children && p.children.some(c => c.toLowerCase().includes(term))) ||
    (p.barcode && p.barcode.includes(term))
  );

  // Filter faculty
  const matchedFaculty = faculty.filter(f =>
    f.name.toLowerCase().includes(term) ||
    (f.role && f.role.toLowerCase().includes(term)) ||
    (f.barcode && f.barcode.includes(term))
  );

  return {
    students: matchedStudents,
    parents: matchedParents,
    faculty: matchedFaculty,
    total: matchedStudents.length + matchedParents.length + matchedFaculty.length
  };
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

export async function clearCollection(userId, collectionName) {
  if (!COLLECTIONS[collectionName]) {
    throw new Error(`Invalid collection: ${collectionName}`);
  }

  const snapshot = await getDocs(getUserCollection(userId, COLLECTIONS[collectionName]));
  const docs = snapshot.docs;

  for (let i = 0; i < docs.length; i += BATCH_LIMIT) {
    const chunk = docs.slice(i, i + BATCH_LIMIT);
    const batch = writeBatch(db);
    chunk.forEach(doc => batch.delete(doc.ref));
    await batch.commit();
  }

  return docs.length;
}

export async function getRosterStats(userId) {
  const [students, parents, faculty] = await Promise.all([
    getAllStudents(userId),
    getAllParents(userId),
    getAllFaculty(userId)
  ]);

  // Count by grade
  const byGrade = {};
  students.forEach(s => {
    byGrade[s.grade] = (byGrade[s.grade] || 0) + 1;
  });

  // Count by teacher
  const byTeacher = {};
  students.forEach(s => {
    byTeacher[s.teacher] = (byTeacher[s.teacher] || 0) + 1;
  });

  // Get unique values
  const grades = [...new Set(students.map(s => s.grade))].sort((a, b) => {
    const order = ['PK3', 'PK4', 'K', '1', '2', '3', '4', '5', '6', '7', '8'];
    return order.indexOf(a) - order.indexOf(b);
  });

  const teachers = [...new Set(students.map(s => s.teacher))].sort();

  return {
    totalStudents: students.length,
    totalParents: parents.length,
    totalFaculty: faculty.length,
    totalPatrons: students.length + parents.length + faculty.length,
    byGrade,
    byTeacher,
    grades,
    teachers
  };
}
